import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useServiceContext } from '../root'
import type { User } from '../services/api/types'
import type { AuthState } from '../services/auth'
import {
  getUser,
  getUserId,
  getToken,
  getDateFormat,
  getTimeFormat,
  getReportsTimeFormat,
  getCurrentLang,
  changeLanguage,
  getUserRoles,
  getStoredAuthInfo,
  refreshUserPolicies,
  USER_ROLES,
  USER_PERMISSIONS
} from '../services/auth'

export const SYSTEM_FOLDER_TYPES = {
  FORMS: 'FORMS',
  TEMPLATES: 'TEMPLATES',
  REGULATED_CONTENT: 'REGULATED_CONTENT',
  WORKFLOWS: 'WORKFLOWS',
  INIT_ETMF_FILES: 'INIT_ETMF_FILES',
  REPORTS: 'REPORTS'
}

export function useAuthService() {
  const queryClient = useQueryClient()
  const { api, service } = useServiceContext()
  const { data: user, isLoading: isUserLoading, error: userError } = service.auth.useCurrentUser()

  const { data: policies, isLoading: isPoliciesLoading } = useQuery({
    queryKey: ['userPolicies'],
    queryFn: () => api.auth.authApiService.getUserPolicies(),
    enabled: !!user,
    staleTime: 5 * 60 * 1000
  })

  const { data: browserFolders, isLoading: isBrowserFoldersLoading } = useQuery({
    queryKey: ['browserFolders'],
    queryFn: () => api.auth.authApiService.getBrowserFolders(),
    enabled: !!user,
    staleTime: 5 * 60 * 1000
  })

  const { data: integrations, isLoading: isIntegrationsLoading } = useQuery({
    queryKey: ['integrations'],
    queryFn: () => api.auth.authApiService.getIntegrations(1),
    enabled: !!user,
    staleTime: 5 * 60 * 1000
  })

  const { data: languages, isLoading: isLanguagesLoading } = useQuery({
    queryKey: ['languages'],
    queryFn: () => api.auth.authApiService.getAllLanguages(),
    enabled: !!user,
    staleTime: 5 * 60 * 1000
  })

  const loginMutation = useMutation<
    { user: Record<string, unknown>; token: string | null },
    Error,
    { email: string; password: string }
  >({
    mutationFn: ({ email, password }: { email: string; password: string }) =>
      api.auth.authApiService.login(email, password) as Promise<{
        user: Record<string, unknown>
        token: string | null
      }>,
    onSuccess: (data: { user: Record<string, unknown>; token: string | null }) => {
      const authInfo: AuthState = {
        user: data.user as unknown as User,
        token: data.token ?? 'cookiebased',
        lang: (data.user as { locale: string }).locale,
        role: (data.user as { role: string }).role,
        expiresAt: null,
        policies: {},
        integrations: {},
        browser: {},
        possibleLanguages: []
      }

      service.auth.storeAuthInfo(authInfo)

      queryClient.invalidateQueries({ queryKey: ['currentUser'] })
      queryClient.invalidateQueries({ queryKey: ['userPolicies'] })
      queryClient.invalidateQueries({ queryKey: ['browserFolders'] })
      queryClient.invalidateQueries({ queryKey: ['integrations'] })
      queryClient.invalidateQueries({ queryKey: ['languages'] })
    }
  })

  const logoutMutation = useMutation({
    mutationFn: () => api.auth.authApiService.logout(),
    onSuccess: () => {
      const authInfo = service.auth.getStoredAuthInfo()
      if (authInfo) {
        authInfo.user = null
        authInfo.token = null
        service.auth.storeAuthInfo(authInfo)
      }

      queryClient.invalidateQueries()
    }
  })

  const changeUserSettingsMutation = useMutation({
    mutationFn: (settings: { timeFormat24h: boolean; locale: string; timeFormat: string; dateFormat: string }) =>
      api.auth.authApiService.changeUserSettings(settings),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['currentUser'] })
    }
  })

  const isAuthorized = (rolesToCheck: string | string[]): boolean => {
    const rolesToCheckArray = Array.isArray(rolesToCheck) ? rolesToCheck : [rolesToCheck]
    const userRole = getRole()

    return !!userRole && rolesToCheckArray.includes(userRole)
  }

  const getBrowserFolder = (systemType: string) => {
    if (!browserFolders?.elements) return null

    return browserFolders.elements.find((folder) => folder.systemFolderType === systemType) || null
  }

  /**
   * Enhanced role determination function that mimics the AngularJS auth.factory.js getRole logic.
   *
   * This function determines the user's effective role based on:
   * 1. Base role from authInfo
   * 2. EOS feature enablement (converts USER to EOS_SITE_USER)
   * 3. Permission-based role determination for CLOUD_ADMIN_ROLE users
   *
   * Role priority for CLOUD_ADMIN_ROLE (checked in order):
   * - manage_etmf → ETMF_MANAGER
   * - manage_study_etmf → ETMF_SPECIALIST
   * - upload_site_artifact → ETMF_SITE_USER
   * - access_study_staging_area → ETMF_STUDY_USER
   * - view_study_etmf → ETMF_SPONSOR
   * - access_rave_content_repository|full_rcr_document|read_rcr_document → RCR_USER
   *
   * @returns The effective user role or null if no auth info available
   */
  const getRole = (): string | null => {
    const authInfo = service.auth.getStoredAuthInfo()
    if (!authInfo?.user || !authInfo.role) return null

    // Check if EOS is enabled and user is a basic USER
    // TODO-EOS: Replace with actual features service when available
    // const featuresInfo = service.init.getFeaturesInfo()
    // const isEosEnabled = featuresInfo?.eosFeature?.show
    const isEosEnabled = false // Temporary until features service is integrated

    if (authInfo.role === USER_ROLES.USER && isEosEnabled) {
      return USER_ROLES.EOS_SITE_USER
    }

    // Handle CLOUD_ADMIN_ROLE with permission-based role determination
    if (authInfo.role === USER_ROLES.CLOUD_ADMIN_ROLE && Array.isArray(authInfo.user.permissions)) {
      const permissions = authInfo.user.permissions as string[]

      // Check permissions in priority order
      if (permissions.includes(USER_PERMISSIONS.manage_etmf)) {
        return USER_ROLES.ETMF_MANAGER
      } else if (permissions.includes(USER_PERMISSIONS.manage_study_etmf)) {
        return USER_ROLES.ETMF_SPECIALIST
      } else if (permissions.includes(USER_PERMISSIONS.upload_site_artifact)) {
        return USER_ROLES.ETMF_SITE_USER
      } else if (permissions.includes(USER_PERMISSIONS.access_study_staging_area)) {
        return USER_ROLES.ETMF_STUDY_USER
      } else if (permissions.includes(USER_PERMISSIONS.view_study_etmf)) {
        return USER_ROLES.ETMF_SPONSOR
      } else if (
        permissions.some((perm: string) =>
          [
            USER_PERMISSIONS.access_rave_content_repository,
            USER_PERMISSIONS.full_rcr_document,
            USER_PERMISSIONS.read_rcr_document
          ].includes(perm)
        )
      ) {
        return USER_ROLES.RCR_USER
      }
    }

    // Return the base role if no special conditions apply
    return authInfo.role
  }

  const isRcrUser = (): boolean => {
    const rcrRoles = [USER_ROLES.RCR_USER]
    const userRole = getRole()
    return !!userRole && rcrRoles.includes(userRole)
  }

  const isStagingUser = (): boolean => {
    const stagingRoles = [
      USER_ROLES.ETMF_MANAGER,
      USER_ROLES.ETMF_SPECIALIST,
      USER_ROLES.ETMF_STUDY_USER,
      USER_ROLES.ETMF_SITE_USER,
      USER_ROLES.ETMF_SPONSOR
    ]
    const userRole = getRole()
    return !!userRole && stagingRoles.includes(userRole)
  }

  const isStagingUploader = (): boolean => {
    const stagingRoles = [USER_ROLES.ETMF_STUDY_USER, USER_ROLES.ETMF_SITE_USER]
    const userRole = getRole()
    return !!userRole && stagingRoles.includes(userRole)
  }

  const getExternalEditorName = (): string | null => {
    if (!integrations?.externalEditor) return null

    switch (integrations.externalEditor) {
      case 'OFFICE':
        return 'INTEGRATION_EDITOR_OFFICE_365'
      case 'GDRIVE':
        return 'INTEGRATION_EDITOR_GOOGLE_DRIVE'
      default:
        return null
    }
  }

  const requireESignatureForFileCommands = (): boolean => {
    return !!policies?.requireESignatureForFileCommands
  }

  const requireUsernameInESignature = (): boolean => {
    return !!policies?.requireUsernameInESignature
  }

  const approverMustViewDocumentPriorToApproval = (): boolean => {
    return !!policies?.approverMustViewDocumentPriorToApproval
  }

  const raUserMustViewDocumentPriorToApproval = (): boolean => {
    return !!policies?.raUserMustViewDocumentPriorToApproval
  }

  return {
    // User data
    user,
    isUserLoading,
    userError,

    // Auth state getters
    getUser: service.auth.getUser,
    getUserId: service.auth.getUserId,
    getToken: service.auth.getToken,
    getRole: getRole, // Use our enhanced role function
    getDateFormat: service.auth.getDateFormat,
    getTimeFormat: service.auth.getTimeFormat,
    getReportsTimeFormat: service.auth.getReportsTimeFormat,
    getCurrentLang: service.auth.getCurrentLang,

    // Auth state setters
    changeLanguage: service.auth.changeLanguage,

    // Auth mutations
    login: loginMutation.mutate,
    isLoginLoading: loginMutation.isPending,
    loginError: loginMutation.error,

    logout: logoutMutation.mutate,
    isLogoutLoading: logoutMutation.isPending,

    changeUserSettings: changeUserSettingsMutation.mutate,
    isChangeUserSettingsLoading: changeUserSettingsMutation.isPending,

    // Policy data
    policies,
    isPoliciesLoading,

    // Browser folders
    browserFolders: browserFolders?.elements || [],
    isBrowserFoldersLoading,
    getBrowserFolder,

    // Integrations
    integrations,
    isIntegrationsLoading,
    getExternalEditorName,

    // Languages
    languages: languages || [],
    isLanguagesLoading,

    // Helper methods
    isAuthorized,
    isRcrUser,
    isStagingUser,
    isStagingUploader,
    requireESignatureForFileCommands,
    requireUsernameInESignature,
    approverMustViewDocumentPriorToApproval,
    raUserMustViewDocumentPriorToApproval
  }
}
export { USER_PERMISSIONS, USER_ROLES }
